import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast, Toaster } from 'sonner';
import { ChefHat, Clock, Flame, Store, Truck, RefreshCw, Bell, Volume2, VolumeX, Maximize2, Minimize2, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hide orders older than this (in hours). Kitchen screens should focus on today's active orders.
const MAX_ORDER_AGE_HOURS = 6;

const BASE_TITLE = 'Pantalla de Cocina';

// Columns shown when the "other statuses" panel is expanded. Status is controlled from Admin/POS.
// Note: "listo" is intentionally NOT shown — once marked ready from POS, the order leaves the kitchen.
const OTHER_COLUMNS = [
  { key: 'preparando', label: 'PREPARANDO', accent: 'yellow', ring: 'border-yellow-500', bg: 'bg-yellow-950/30', header: 'bg-yellow-600' },
];

// All valid kitchen statuses (used to filter orders in this view)
const KITCHEN_STATUSES = ['recibido', 'preparando'];

// Delivery type columns for the NUEVOS view (split by ventanilla vs domicilio)
const NEW_TYPE_COLUMNS = [
  { key: 'ventanilla', label: 'VENTANILLA',      ring: 'border-cyan-500',   bg: 'bg-cyan-950/30',   header: 'bg-cyan-600' },
  { key: 'domicilio',  label: 'ENVÍO A DOMICILIO', ring: 'border-orange-500', bg: 'bg-orange-950/30', header: 'bg-orange-600' },
];

// Compute minutes elapsed since a given ISO date
const minutesSince = (iso) => {
  if (!iso) return 0;
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((now - then) / 60000));
  } catch (e) {
    return 0;
  }
};

// Urgency color for elapsed time badge
const elapsedColor = (mins) => {
  if (mins >= 20) return 'bg-red-600 text-white animate-pulse';
  if (mins >= 10) return 'bg-orange-500 text-white';
  return 'bg-gray-700 text-gray-200';
};

export const KitchenPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem('kitchen_sound') !== '0'; } catch (e) { return true; }
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showOtherColumns, setShowOtherColumns] = useState(false);
  const [newOrderAlert, setNewOrderAlert] = useState(false);
  const prevOrderIdsRef = useRef(new Set());
  const audioCtxRef = useRef(null);
  const firstLoadRef = useRef(true);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/orders`);
      if (res.ok) {
        const data = await res.json();
        setOrders(Array.isArray(data) ? data : []);
        setLastRefresh(new Date());
      }
    } catch (err) {
      console.error('Error fetching orders', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const timer = setInterval(fetchOrders, 10000);
    return () => clearInterval(timer);
  }, [fetchOrders]);

  // Force re-render every 30s so timers update
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // Kitchen orders: exclude entregado, cancelado, reembolsado + hide orders older than MAX_ORDER_AGE_HOURS
  const kitchenOrders = useMemo(() => {
    const cutoff = Date.now() - (MAX_ORDER_AGE_HOURS * 60 * 60 * 1000);
    return orders.filter(o => {
      if (!KITCHEN_STATUSES.includes(o.status)) return false;
      if (o.payment_status === 'reembolsado') return false;
      const created = new Date(o.created_at).getTime();
      if (!created || Number.isNaN(created)) return false;
      return created >= cutoff;
    });
  }, [orders]);

  // Group NEW orders (recibido) by delivery_type
  const newOrdersByType = useMemo(() => {
    const grouped = { ventanilla: [], domicilio: [] };
    kitchenOrders
      .filter(o => o.status === 'recibido')
      .forEach(o => {
        const key = o.delivery_type === 'domicilio' ? 'domicilio' : 'ventanilla';
        grouped[key].push(o);
      });
    Object.keys(grouped).forEach(k => {
      grouped[k].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    });
    return grouped;
  }, [kitchenOrders]);

  // Group PREPARANDO/LISTOS by status
  const otherOrdersByStatus = useMemo(() => {
    const grouped = {};
    OTHER_COLUMNS.forEach(c => { grouped[c.key] = []; });
    kitchenOrders.forEach(o => {
      if (grouped[o.status]) grouped[o.status].push(o);
    });
    Object.keys(grouped).forEach(k => {
      grouped[k].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    });
    return grouped;
  }, [kitchenOrders]);

  // Sound: bell-like beep using Web Audio API
  const playAlertBeep = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const playTone = (freq, startTime, duration = 0.25) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.5, startTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };
      const now = ctx.currentTime;
      for (let i = 0; i < 3; i++) {
        const offset = i * 0.55;
        playTone(880, now + offset, 0.22);
        playTone(660, now + offset + 0.22, 0.28);
      }
    } catch (err) {
      console.warn('Audio playback failed', err);
    }
  }, [soundEnabled]);

  // Alert on new orders + play sound (skip first load)
  useEffect(() => {
    const currentIds = new Set(kitchenOrders.filter(o => o.status === 'recibido').map(o => o.id));
    const prev = prevOrderIdsRef.current;
    if (!firstLoadRef.current) {
      const newIds = [...currentIds].filter(id => !prev.has(id));
      if (newIds.length > 0) {
        toast.success(`🔔 ${newIds.length} pedido(s) nuevo(s)`);
        playAlertBeep();
        // Only flash the browser title if this tab is not currently focused
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          setNewOrderAlert(true);
        }
      }
    } else if (kitchenOrders.length > 0) {
      firstLoadRef.current = false;
    }
    prevOrderIdsRef.current = currentIds;
  }, [kitchenOrders, playAlertBeep]);

  // Blinking title while tab is unfocused after a new order
  useEffect(() => {
    if (!newOrderAlert) {
      document.title = BASE_TITLE;
      return;
    }
    let toggle = false;
    const timer = setInterval(() => {
      toggle = !toggle;
      document.title = toggle ? '🔔 ¡NUEVO PEDIDO! 🔔' : BASE_TITLE;
    }, 800);
    return () => {
      clearInterval(timer);
      document.title = BASE_TITLE;
    };
  }, [newOrderAlert]);

  // Clear alert when user focuses tab
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setNewOrderAlert(false);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, []);

  useEffect(() => {
    try { localStorage.setItem('kitchen_sound', soundEnabled ? '1' : '0'); } catch (e) { /* ignore */ }
  }, [soundEnabled]);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    if (next) {
      playAlertBeep();
      toast.success('🔊 Sonido activado');
    } else {
      toast.info('🔇 Sonido desactivado');
    }
  };

  // Fullscreen support (cross-browser)
  useEffect(() => {
    const onChange = () => {
      const el = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement;
      setIsFullscreen(!!el);
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      const isFs = document.fullscreenElement || document.webkitFullscreenElement;
      if (!isFs) {
        const el = document.documentElement;
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
        else if (el.mozRequestFullScreen) await el.mozRequestFullScreen();
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
      }
    } catch (err) {
      console.warn('Fullscreen toggle failed', err);
      toast.error('No se pudo activar pantalla completa');
    }
  };

  // Render a single order card (used in Ventanilla, Domicilio, Preparando, Listos)
  const renderOrderCard = (order) => {
    const mins = minutesSince(order.created_at);
    const isDelivery = order.delivery_type === 'domicilio';
    return (
      <div
        key={order.id}
        className="bg-gray-900 border-2 border-gray-700 rounded-xl overflow-hidden shadow-lg"
        data-testid={`kitchen-order-${order.id}`}
      >
        <div className="bg-black/50 px-3 py-2 flex justify-between items-center border-b border-gray-800">
          <span className="text-orange-400 font-black text-xl font-mono tracking-wide">
            #{order.id?.slice(0,6).toUpperCase()}
          </span>
          <span className={`${elapsedColor(mins)} px-2.5 py-1 rounded-md text-base font-bold flex items-center gap-1`}>
            <Clock className="h-4 w-4" /> {mins}m
          </span>
        </div>

        <div className="px-3 py-2 border-b border-gray-800">
          <div className="flex items-center justify-between gap-2">
            <p className="text-white font-bold text-lg truncate">{order.customer_name || '(sin nombre)'}</p>
            <span className={`shrink-0 text-sm font-bold flex items-center gap-1 ${isDelivery ? 'text-orange-400' : 'text-cyan-400'}`}>
              {isDelivery ? <Truck className="h-4 w-4" /> : <Store className="h-4 w-4" />}
              {isDelivery ? 'Domicilio' : 'Ventanilla'}
            </span>
          </div>
        </div>

        {order.referrer_vip_id && (order.subtotal || 0) >= 150 && (
          <div className="mx-2 my-2 bg-orange-500/25 border-2 border-orange-500 rounded-lg px-3 py-2 text-center">
            <p className="text-orange-200 font-black text-base">🌯 INCLUIR BURRITO GRATIS</p>
            <p className="text-orange-300 text-xs">Promoción por recomendación</p>
          </div>
        )}

        <div className="px-3 py-2.5 space-y-2">
          {order.items?.map((item, idx) => (
            <div key={idx} className="border-b border-gray-800 pb-2 last:border-b-0 last:pb-0">
              <div className="flex items-baseline gap-3">
                <span className="text-orange-400 font-black text-3xl leading-none w-10 shrink-0 text-right">
                  {item.quantity}×
                </span>
                <span className="text-white font-bold text-xl leading-tight break-words">
                  {item.name}
                </span>
              </div>
              {item.notes && item.notes.trim() && (
                <div className="ml-12 mt-1 bg-yellow-500/20 border border-yellow-500/70 rounded-md px-2 py-1 flex items-start gap-1.5">
                  <Flame className="h-4 w-4 shrink-0 mt-0.5 text-yellow-400" />
                  <span className="text-yellow-200 text-base font-bold leading-tight whitespace-pre-wrap break-words">
                    {item.notes}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {order.notes && order.notes.trim() && (
          <div className="mx-2 mb-2 bg-yellow-500/20 border-2 border-yellow-500 rounded-lg px-3 py-2">
            <div className="flex items-start gap-2">
              <Flame className="h-5 w-5 shrink-0 mt-0.5 text-yellow-400" />
              <div>
                <p className="text-yellow-400 text-[11px] uppercase font-black tracking-wider mb-0.5">Nota general</p>
                <p className="text-yellow-200 text-lg font-bold leading-tight whitespace-pre-wrap break-words">
                  {order.notes}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-4 flex flex-col" data-testid="kitchen-page">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <ChefHat className="h-9 w-9 text-orange-500" />
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white leading-none">Pantalla de Cocina</h1>
            <p className="text-gray-500 text-xs mt-0.5">
              Actualizado {lastRefresh.toLocaleTimeString('es-MX')} · Auto 10s · Solo lectura · Últimas {MAX_ORDER_AGE_HOURS}h
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={toggleFullscreen}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold border-2 transition-all ${
              isFullscreen
                ? 'bg-orange-600/20 border-orange-500 text-orange-300 hover:bg-orange-600/30'
                : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
            }`}
            data-testid="btn-toggle-fullscreen"
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            {isFullscreen ? 'Salir' : 'Pantalla Completa'}
          </button>
          <button
            onClick={toggleSound}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold border-2 transition-all ${
              soundEnabled
                ? 'bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30'
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
            }`}
            data-testid="btn-toggle-sound"
            title={soundEnabled ? 'Desactivar sonido' : 'Activar sonido'}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            {soundEnabled ? 'Sonido ON' : 'Sonido OFF'}
          </button>
          <button
            onClick={fetchOrders}
            className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold"
            data-testid="btn-refresh-kitchen"
          >
            <RefreshCw className="h-4 w-4" /> Actualizar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400 text-xl">Cargando pedidos...</div>
      ) : kitchenOrders.length === 0 ? (
        <div className="text-center py-24 border-2 border-dashed border-gray-800 rounded-2xl flex-1 flex flex-col items-center justify-center">
          <Bell className="h-20 w-20 text-gray-700 mx-auto mb-4" />
          <p className="text-gray-400 text-2xl font-bold">Sin pedidos por preparar</p>
          <p className="text-gray-600 text-base mt-1">
            Los pedidos nuevos aparecerán aquí automáticamente. Solo se muestran pedidos de las últimas {MAX_ORDER_AGE_HOURS} horas.
          </p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 gap-3">
          {/* Main area: two columns (Ventanilla + Domicilio) for NUEVOS */}
          <div className="grid gap-3 flex-1 min-h-0 grid-cols-1 md:grid-cols-2">
            {NEW_TYPE_COLUMNS.map(col => {
              const list = newOrdersByType[col.key] || [];
              const Icon = col.key === 'domicilio' ? Truck : Store;
              return (
                <div
                  key={col.key}
                  className={`flex flex-col ${col.bg} border-2 ${col.ring} rounded-2xl overflow-hidden`}
                  data-testid={`kitchen-newcol-${col.key}`}
                >
                  <div className={`${col.header} px-4 py-2.5 flex items-center justify-between`}>
                    <h2 className="text-white font-black text-xl tracking-wide flex items-center gap-2">
                      <Icon className="h-6 w-6" />
                      {col.label}
                    </h2>
                    <span className="bg-black/40 text-white text-base font-black px-3 py-0.5 rounded-full min-w-[2.5rem] text-center">
                      {list.length}
                    </span>
                  </div>

                  <div className="flex-1 overflow-y-auto p-2">
                    {list.length === 0 ? (
                      <div className="text-center py-8 text-gray-600 text-sm italic">
                        Sin pedidos
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {list.map(order => renderOrderCard(order))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expandable panel for Preparando */}
          {showOtherColumns && (
            <div className="grid gap-3 grid-cols-1 min-h-0" style={{ maxHeight: '50vh' }}>
              {OTHER_COLUMNS.map(col => {
                const list = otherOrdersByStatus[col.key] || [];
                return (
                  <div
                    key={col.key}
                    className={`flex flex-col ${col.bg} border-2 ${col.ring} rounded-2xl overflow-hidden`}
                    data-testid={`kitchen-col-${col.key}`}
                  >
                    <div className={`${col.header} px-4 py-2 flex items-center justify-between`}>
                      <h2 className="text-white font-black text-lg tracking-wide">{col.label}</h2>
                      <span className="bg-black/40 text-white text-sm font-black px-3 py-0.5 rounded-full min-w-[2.5rem] text-center">
                        {list.length}
                      </span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2">
                      {list.length === 0 ? (
                        <div className="text-center py-6 text-gray-600 text-sm italic">Sin pedidos</div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {list.map(order => renderOrderCard(order))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Expand/collapse bar for other statuses */}
          <button
            onClick={() => setShowOtherColumns(v => !v)}
            className={`w-full py-2.5 rounded-xl font-bold text-base flex items-center justify-center gap-2 border-2 transition-all ${
              showOtherColumns
                ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                : 'bg-gray-900 border-gray-800 text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
            data-testid="btn-toggle-other-columns"
          >
            {showOtherColumns ? (
              <>
                <ChevronUp className="h-5 w-5" /> Ocultar Preparando
              </>
            ) : (
              <>
                <ChevronDown className="h-5 w-5" />
                Ver Preparando ({(otherOrdersByStatus.preparando || []).length})
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default KitchenPage;
